import React from 'react';
import {
  Breadcrumbs as MuiBreadcrumbs,
  Link,
  Typography,
  Box,
  useTheme,
  alpha
} from '@mui/material';
import {
  NavigateNext as NavigateNextIcon,
  Home as HomeIcon,
  ChevronRight as ChevronRightIcon
} from '@mui/icons-material';
import { Link as RouterLink, useLocation } from 'react-router-dom';

export interface BreadcrumbItem {
  label: string;
  href?: string;
  icon?: React.ReactNode;
}

interface BreadcrumbsProps {
  items: BreadcrumbItem[];
  showHome?: boolean;
  homeHref?: string;
  separator?: 'arrow' | 'slash' | 'chevron';
  maxItems?: number;
  variant?: 'default' | 'contained';
}

/**
 * Navigation Breadcrumbs component
 *
 * Usage:
 * <Breadcrumbs
 *   items={[
 *     { label: 'Jobs', href: '/admin/jobs' },
 *     { label: 'Software Engineer' }
 *   ]}
 * />
 */
export const Breadcrumbs: React.FC<BreadcrumbsProps> = ({
  items,
  showHome = true,
  homeHref = '/admin/dashboard',
  separator = 'chevron',
  maxItems = 4,
  variant = 'default'
}) => {
  const theme = useTheme();

  const getSeparator = () => {
    switch (separator) {
      case 'arrow':
        return <NavigateNextIcon fontSize="small" sx={{ color: 'text.disabled' }} />;
      case 'slash':
        return <Typography color="text.disabled">/</Typography>;
      case 'chevron':
      default:
        return <ChevronRightIcon fontSize="small" sx={{ color: 'text.disabled' }} />;
    }
  };

  const allItems: BreadcrumbItem[] = showHome
    ? [{ label: 'Home', href: homeHref, icon: <HomeIcon fontSize="small" /> }, ...items]
    : items;

  if (variant === 'contained') {
    return (
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          bgcolor: alpha(theme.palette.primary.main, 0.08),
          borderRadius: 2,
          px: 2,
          py: 0.75
        }}
      >
        <MuiBreadcrumbs
          separator={getSeparator()}
          maxItems={maxItems}
          aria-label="breadcrumb"
          sx={{
            '& .MuiBreadcrumbs-separator': {
              mx: 1
            }
          }}
        >
          {allItems.map((item, index) => {
            const isLast = index === allItems.length - 1;

            if (isLast) {
              return (
                <Typography
                  key={index}
                  variant="body2"
                  fontWeight={600}
                  color="primary"
                  sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                >
                  {item.icon}
                  {item.label}
                </Typography>
              );
            }

            return (
              <Link
                key={index}
                component={RouterLink}
                to={item.href || '#'}
                underline="hover"
                color="text.secondary"
                variant="body2"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  transition: 'color 0.2s',
                  '&:hover': {
                    color: 'primary.main'
                  }
                }}
              >
                {item.icon}
                {item.label}
              </Link>
            );
          })}
        </MuiBreadcrumbs>
      </Box>
    );
  }

  return (
    <MuiBreadcrumbs
      separator={getSeparator()}
      maxItems={maxItems}
      aria-label="breadcrumb"
      sx={{
        mb: 2,
        '& .MuiBreadcrumbs-separator': {
          mx: 0.75
        }
      }}
    >
      {allItems.map((item, index) => {
        const isLast = index === allItems.length - 1;

        if (isLast) {
          return (
            <Typography
              key={index}
              variant="body2"
              fontWeight={500}
              color="text.primary"
              sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
            >
              {item.icon}
              {item.label}
            </Typography>
          );
        }

        return (
          <Link
            key={index}
            component={RouterLink}
            to={item.href || '#'}
            underline="hover"
            color="text.secondary"
            variant="body2"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              transition: 'color 0.2s',
              '&:hover': {
                color: 'primary.main'
              }
            }}
          >
            {item.icon}
            {item.label}
          </Link>
        );
      })}
    </MuiBreadcrumbs>
  );
};

/**
 * Auto-generated breadcrumbs based on current route
 * Useful for consistent navigation patterns
 */
interface AutoBreadcrumbsProps {
  customLabels?: Record<string, string>;
  showHome?: boolean;
}

// Route to label mapping
const defaultRouteLabels: Record<string, string> = {
  admin: 'Admin',
  dashboard: 'Dashboard',
  jobs: 'Jobs',
  post: 'Post Job',
  edit: 'Edit',
  applications: 'Applications',
  analytics: 'Analytics',
  settings: 'Settings',
  profile: 'Profile',
  'super-admin': 'Super Admin',
  'bulk-matching': 'Bulk Matching',
  calls: 'Screening Calls',
  'call-logs': 'Call Logs',
  interviews: 'Interviews'
};

export const AutoBreadcrumbs: React.FC<AutoBreadcrumbsProps> = ({
  customLabels = {},
  showHome = true
}) => {
  const location = useLocation();
  const pathnames = location.pathname.split('/').filter((x) => x);

  const labels = { ...defaultRouteLabels, ...customLabels };

  const items: BreadcrumbItem[] = pathnames
    .filter((value) => value !== 'admin') // Skip 'admin' from breadcrumbs as Home leads there
    .map((value, index) => {
      // Check if this looks like an ID (UUID or numeric)
      const isId = /^[0-9a-f-]{36}$/i.test(value) || /^\d+$/.test(value);

      // Build the href up to this point
      const pathTo = `/${pathnames.slice(0, pathnames.indexOf(value) + 1).join('/')}`;

      return {
        label: isId ? 'Details' : labels[value] || value.charAt(0).toUpperCase() + value.slice(1).replace(/-/g, ' '),
        href: pathTo
      };
    });

  // Remove href from last item (current page)
  if (items.length > 0) {
    delete items[items.length - 1].href;
  }

  return <Breadcrumbs items={items} showHome={showHome} />;
};

export default Breadcrumbs;
